import React, { useState, useRef, useEffect } from 'react';
import { Check, X, User, PackageCheck } from 'lucide-react';
import ConfirmationSlider from '../../../components/ConfirmationSlider';
import { useSales } from '../../../contexts/SalesContext';

// Temporal: Esto debería venir de un contexto o prop
const DELIVERY_STAFF = [
  { id: 1, name: 'Juan', color: 'bg-blue-500' },
  { id: 2, name: 'María', color: 'bg-purple-500' },
  { id: 3, name: 'Carlos', color: 'bg-orange-500' },
  { id: 4, name: 'Ana', color: 'bg-green-500' },
];

const DeliveryStatusModal = ({ 
  isOpen, 
  onClose, 
  products, 
  onDeliveryConfirm,
  sale // Necesitamos los datos de la venta
}) => {
  const { updatePreparedItems, getPreparedItems } = useSales();
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [preparedProducts, setPreparedProducts] = useState(new Set());

  // Cargar los items preparados cuando se abre el modal
  useEffect(() => {
    if (isOpen && sale) {
      const prepared = new Set(getPreparedItems(sale.id));
      setPreparedProducts(prepared);
    }
  }, [isOpen, sale]);

  const handleTogglePrepared = (productId) => {
    const newPrepared = new Set(preparedProducts);
    const isPrepared = !preparedProducts.has(productId);
    
    if (isPrepared) {
      newPrepared.add(productId);
    } else {
      newPrepared.delete(productId);
    }
    
    setPreparedProducts(newPrepared);
    updatePreparedItems(sale.id, productId, isPrepared);
  };

  const allProductsPrepared = products?.length > 0 && 
    products.every(product => preparedProducts.has(product.id));

  const canConfirmDelivery = allProductsPrepared && selectedStaff;

  const handleDeliveryConfirm = () => {
    if (!canConfirmDelivery) return;
    
    onDeliveryConfirm({
      staffName: selectedStaff.name,
      timestamp: new Date().toISOString(),
      preparedItems: Array.from(preparedProducts)
    });
    onClose();
  };

  if (!isOpen) return null;

  // Formateo simple de fecha temporal
  const formattedDate = sale?.created_at ? 
    new Date(sale.created_at).toLocaleString('es-AR', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    }) : '';

  return (
    <>
      {/* Overlay con fade in suave */}
      <div 
        className="fixed inset-0 bg-black transition-opacity duration-200 ease-out z-40"
        style={{ opacity: isOpen ? 0.5 : 0 }}
        onClick={onClose}
      />
      
      {/* Bottom sheet con slide up suave */}
      <div 
        className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-3xl z-50 transform transition-all duration-200 ease-out shadow-lg flex flex-col"
        style={{ 
          height: '80vh',
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          opacity: isOpen ? 1 : 0
        }}
      >
        {/* Handle y Header */}
        <div className="flex-shrink-0 sticky top-0 bg-white dark:bg-gray-800 rounded-t-3xl border-b dark:border-gray-700">
          <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto my-3" />
          
          {/* Header con información de la venta */}
          <div className="px-6 pb-4">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  VENTA #{sale?.display_id}
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 mt-1">
                  {sale?.client?.name || 'Sin cliente'}
                </p>
                <p className="text-base text-gray-500 dark:text-gray-400">
                  {formattedDate}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <X className="w-7 h-7 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Staff selector chips */}
            <div className="mt-6">
              <p className="text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                RESPONSABLE DE ENTREGA
              </p>
              <div className="flex flex-wrap gap-3">
                {DELIVERY_STAFF.map(staff => (
                  <button
                    key={staff.id}
                    onClick={() => setSelectedStaff(staff)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      selectedStaff?.id === staff.id
                        ? 'bg-green-50 dark:bg-green-900/30 ring-1 ring-green-500 dark:ring-green-600'
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-base font-medium transition-all ${
                      selectedStaff?.id === staff.id
                        ? staff.color
                        : `${staff.color} opacity-50`
                    }`}>
                      {staff.name.charAt(0)}
                    </div>
                    <span className={`text-base font-medium transition-colors ${
                      selectedStaff?.id === staff.id
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {staff.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Lista scrolleable */}
        <div className="flex-1 overflow-y-auto px-6">
          <div className="py-4 space-y-4">
            <p className="text-base font-medium text-gray-700 dark:text-gray-300">
              PRODUCTOS A ENTREGAR ({preparedProducts.size}/{products?.length})
            </p>
            {products?.map((product) => {
              console.log('Product in modal:', product);
              return (
                <button
                  key={product.id}
                  onClick={() => handleTogglePrepared(product.id)}
                  className={`w-full p-5 rounded-xl transition-all ${
                    preparedProducts.has(product.id)
                      ? 'bg-green-50 dark:bg-green-900/30 border-2 border-green-500 dark:border-green-600 scale-[1.02]'
                      : 'bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            {product.lot?.name || product.lot?.title || product.title}
                          </h3>
                          {product.is_lightning_lot && (
                            <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-medium rounded-lg">
                              ⚡ Relámpago
                            </span>
                          )}
                        </div>
                        <span className="text-3xl font-bold text-gray-900 dark:text-white">
                          {product.quantity}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-600 rounded-lg text-base text-gray-600 dark:text-gray-300">
                          Lote #{product.lot?.display_id}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-600 rounded-lg text-base text-gray-600 dark:text-gray-300">
                          #{product.displayId}
                        </span>
                      </div>
                      <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                        {product.description}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                      preparedProducts.has(product.id)
                        ? 'bg-green-500 dark:bg-green-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      <Check className={`w-7 h-7 ${
                        preparedProducts.has(product.id)
                          ? 'text-white'
                          : 'text-gray-500 dark:text-gray-400'
                      }`} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Botones fijos abajo */}
        <div className="flex-shrink-0 sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t dark:border-gray-700">
          <div className="space-y-4">
            {/* Indicador de estado de preparación */}
            <div className={`w-full py-5 rounded-xl text-center ${
              allProductsPrepared
                ? 'bg-blue-50 dark:bg-blue-900/30'
                : 'bg-gray-50 dark:bg-gray-800'
            }`}>
              {allProductsPrepared ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-3">
                    <PackageCheck className="w-8 h-8 text-blue-600 dark:text-blue-500" />
                    <span className="text-xl font-bold text-blue-600 dark:text-blue-500">
                      ¡Productos listos para entregar!
                    </span>
                  </div>
                  <span className="text-base text-blue-600/80 dark:text-blue-500/80">
                    {preparedProducts.size}/{products?.length} productos preparados
                  </span>
                </div>
              ) : (
                <div className="space-y-1">
                  <span className="text-xl font-bold text-gray-600 dark:text-gray-400">
                    {preparedProducts.size}/{products?.length} productos preparados
                  </span>
                  <div className="text-base text-gray-500 dark:text-gray-500">
                    Marca los productos a medida que los prepares
                  </div>
                </div>
              )}
            </div>

            {/* Slider de confirmación */}
            {canConfirmDelivery ? (
              <ConfirmationSlider 
                onConfirm={handleDeliveryConfirm}
                text="DESLIZA PARA CONFIRMAR"
              />
            ) : (
              <div className="h-[60px] bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 font-medium text-lg">
                {!allProductsPrepared ? (
                  'Preparar todos los productos primero'
                ) : (
                  'Seleccionar responsable de entrega'
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default DeliveryStatusModal; 